import {
  DocumentChangeType,
  driveCollectionId,
  PropagationMode,
  type IReactorClient,
  type PollBehavior,
} from "@powerhousedao/reactor";
import {
  driveCreateDocument,
  setAvailableOffline,
  setSharingType,
  type DocumentDriveDocument,
  type DriveInput,
  type SharingType,
} from "@powerhousedao/shared/document-drive";
import type { PHDocument } from "@powerhousedao/shared/document-model";
import { getUserPermissions } from "../utils/user.js";

const DEFAULT_INITIAL_SYNC_TIMEOUT_MS = 30_000;

export type AddRemoteDriveOptions = {
  pollBehavior?: PollBehavior;
  /**
   * When true, wait for the drive document to be materialized locally
   * (i.e. queryable via the reactor) before resolving. Without this,
   * `addRemoteDrive` returns as soon as the remote is registered with
   * the sync manager, before initial backfill delivers the drive.
   */
  awaitInitialSync?: boolean;
  /** Timeout for the initial-sync wait. Defaults to 30s. */
  initialSyncTimeoutMs?: number;
  signal?: AbortSignal;
};

/**
 * Resolves once a document with the given id is queryable through the
 * reactor client. Subscribes to Created events filtered by id and
 * short-circuits if the document already exists.
 */
export async function waitForDocumentReady(
  reactorClient: IReactorClient,
  documentId: string,
  options?: { timeoutMs?: number; signal?: AbortSignal },
): Promise<void> {
  const timeoutMs = options?.timeoutMs ?? DEFAULT_INITIAL_SYNC_TIMEOUT_MS;
  const signal = options?.signal;

  return new Promise<void>((resolve, reject) => {
    let settled = false;
    // eslint-disable-next-line prefer-const
    let unsubscribe: (() => void) | undefined;
    // eslint-disable-next-line prefer-const
    let timer: ReturnType<typeof setTimeout> | undefined;
    let abortHandler: (() => void) | undefined;

    const settle = (action: () => void) => {
      if (settled) return;
      settled = true;
      unsubscribe?.();
      if (timer) clearTimeout(timer);
      if (abortHandler && signal) {
        signal.removeEventListener("abort", abortHandler);
      }
      action();
    };

    unsubscribe = reactorClient.subscribe({ ids: [documentId] }, (event) => {
      if (event.type === DocumentChangeType.Created) {
        settle(() => resolve());
      }
    });

    reactorClient
      .find({ ids: [documentId] })
      .then((existing) => {
        if (existing.results.length > 0) {
          settle(() => resolve());
        }
      })
      .catch(() => {
        // Ignore: the subscription will still resolve if the document arrives.
      });

    if (signal) {
      if (signal.aborted) {
        settle(() => reject(new DOMException("Aborted", "AbortError")));
        return;
      }
      abortHandler = () => {
        settle(() => reject(new DOMException("Aborted", "AbortError")));
      };
      signal.addEventListener("abort", abortHandler);
    }

    timer = setTimeout(() => {
      settle(() =>
        reject(
          new Error(
            `Timed out after ${timeoutMs}ms waiting for document ${documentId}`,
          ),
        ),
      );
    }, timeoutMs);
  });
}

export async function addDrive(input: DriveInput, preferredEditor?: string) {
  const { isAllowedToCreateDocuments } = getUserPermissions();
  if (!isAllowedToCreateDocuments) {
    throw new Error("User is not allowed to create drives");
  }

  const reactorClient = window.ph?.reactorClient;
  if (!reactorClient) {
    throw new Error("ReactorClient not initialized");
  }

  const driveDoc = driveCreateDocument({
    global: {
      name: input.global.name || "",
      icon: input.global.icon ?? null,
      nodes: [],
    },
  });

  if (preferredEditor) {
    driveDoc.header.meta = { preferredEditor };
  }

  return await reactorClient.create<DocumentDriveDocument>(driveDoc);
}

export async function addRemoteDrive(
  url: string,
  driveId?: string,
  options?: AddRemoteDriveOptions,
) {
  const reactorClient = window.ph?.reactorClient;
  if (!reactorClient) {
    throw new Error("ReactorClient not initialized");
  }

  const sync =
    window.ph?.reactorClientModule?.reactorModule?.syncModule?.syncManager;
  if (!sync) {
    throw new Error("Sync not initialized");
  }

  // Fetch drive info from the REST endpoint to get both id and graphqlEndpoint
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to resolve drive info from ${url}`);
  }
  const driveInfo = (await response.json()) as {
    id: string;
    graphqlEndpoint: string;
  };

  const resolvedDriveId = driveId ?? driveInfo.id;
  const collectionId = driveCollectionId("main", resolvedDriveId);

  const existingRemote = sync
    .list()
    .find((remote) => remote.collectionId === collectionId);

  if (!existingRemote) {
    const remoteName = crypto.randomUUID();
    await sync.add(
      remoteName,
      collectionId,
      {
        type: "gql",
        parameters: {
          url: driveInfo.graphqlEndpoint,
        },
      },
      undefined,
      options?.pollBehavior
        ? { pollBehavior: options.pollBehavior }
        : undefined,
    );
  }

  if (options?.awaitInitialSync) {
    await waitForDocumentReady(reactorClient, resolvedDriveId, {
      timeoutMs: options.initialSyncTimeoutMs,
      signal: options.signal,
    });
  }

  return resolvedDriveId;
}

export async function deleteDrive(driveId: string) {
  const { isAllowedToCreateDocuments } = getUserPermissions();
  if (!isAllowedToCreateDocuments) {
    throw new Error("User is not allowed to delete drives");
  }

  const reactorClient = window.ph?.reactorClient;
  if (!reactorClient) {
    throw new Error("ReactorClient not initialized");
  }

  const sync =
    window.ph?.reactorClientModule?.reactorModule?.syncModule?.syncManager;
  if (sync) {
    const collectionId = driveCollectionId("main", driveId);
    const remotes = sync
      .list()
      .filter((remote) => remote.collectionId === collectionId);
    for (const remote of remotes) {
      await sync.remove(remote.name);
    }
  }

  await reactorClient.deleteDocument(driveId, PropagationMode.Cascade);
}

export async function renameDrive(
  driveId: string,
  name: string,
): Promise<PHDocument | undefined> {
  const { isAllowedToCreateDocuments } = getUserPermissions();
  if (!isAllowedToCreateDocuments) {
    throw new Error("User is not allowed to rename drives");
  }

  const reactorClient = window.ph?.reactorClient;
  if (!reactorClient) {
    throw new Error("ReactorClient not initialized");
  }
  return await reactorClient.rename(driveId, name);
}

export async function setDriveAvailableOffline(
  driveId: string,
  availableOffline: boolean,
): Promise<PHDocument | undefined> {
  const { isAllowedToCreateDocuments } = getUserPermissions();
  if (!isAllowedToCreateDocuments) {
    throw new Error("User is not allowed to change drive availability");
  }

  const reactorClient = window.ph?.reactorClient;
  if (!reactorClient) {
    throw new Error("ReactorClient not initialized");
  }
  return await reactorClient.execute(driveId, "main", [
    setAvailableOffline({ availableOffline }),
  ]);
}

export async function setDriveSharingType(
  driveId: string,
  sharingType: SharingType,
): Promise<PHDocument | undefined> {
  const { isAllowedToCreateDocuments } = getUserPermissions();
  if (!isAllowedToCreateDocuments) {
    throw new Error("User is not allowed to change drive sharing type");
  }

  const reactorClient = window.ph?.reactorClient;
  if (!reactorClient) {
    throw new Error("ReactorClient not initialized");
  }
  return await reactorClient.execute(driveId, "main", [
    setSharingType({ type: sharingType }),
  ]);
}
